/**
 * Barra lateral fixa da home — só no computador (≥1024px).
 *
 * Memória: `.claude/.../memory/project_delivery_app_idiomas_e_desktop.md`
 *
 * Por que existe: o app nasceu mobile-first e no computador ficava uma
 * coluna de 480px no meio de uma tela de 1920px — ~75% da tela em cinza,
 * e o cliente rolando uma fita de categorias que caberia inteira na
 * lateral. Cardápio de restaurante grande (o pedido veio de um com 20+
 * categorias) é exatamente onde isso dói.
 *
 * É a MESMA `CategoryNav` da gaveta do celular, num invólucro diferente —
 * ver o comentário de lá sobre por que não são dois componentes.
 *
 * Nada aqui aparece no celular (`hidden lg:flex`), então o risco pro
 * cliente que pede pelo telefone — a maioria — é zero.
 */
import type { MenuCategory } from '../types';
import { CategoryNav } from './CategoryNav';
import { LanguagePicker } from './LanguagePicker';
import { useI18n } from '../i18n';
import { TrocarUnidade } from './TrocarUnidade';

interface Props {
  categories: MenuCategory[];
  storeName: string;
  storeLogo: string | null;
  isOpen: boolean;
  deliveryEstimate: string;
}

export function DesktopSidebar({ categories, storeName, storeLogo, isOpen, deliveryEstimate }: Props) {
  // assina a troca de idioma (ver i18n.ts)
  useI18n();
  const { t } = useI18n();

  return (
    <aside className="hidden lg:flex flex-col w-[290px] shrink-0 h-screen sticky top-0 bg-white border-r border-gray-100">
      {/* Identidade da loja */}
      <div
        className="px-5 pt-5 pb-3 shrink-0 cursor-pointer"
        onClick={() => window.goBackToHome?.()}
      >
        <div className="w-16 h-16 bg-white rounded-full p-1 shadow-md ring-1 ring-gray-100 mb-3">
          {storeLogo ? (
            <img
              src={storeLogo}
              alt={storeName}
              className="w-full h-full rounded-full object-contain"
              onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://placehold.co/100x100?text=Logo'; }}
            />
          ) : (
            <div className="w-full h-full rounded-full bg-red-50 text-red-500 flex items-center justify-center">
              <i className="fas fa-utensils text-xl" />
            </div>
          )}
        </div>
        <h2 className="text-base font-black text-gray-800 leading-tight break-words">{storeName}</h2>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${isOpen ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className={`text-[11px] font-bold ${isOpen ? 'text-green-700' : 'text-red-600'}`}>
              {isOpen ? t('Loja Aberta') : t('Fechados no momento')}
            </span>
          </span>
          {deliveryEstimate && (
            <span className="text-[11px] font-bold text-gray-400 flex items-center gap-1">
              <i className="far fa-clock" /> {deliveryEstimate}
            </span>
          )}
        </div>
      </div>

      <div className="px-5 pb-2 shrink-0">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('Categorias')}</p>
      </div>

      {/* A lista rola sozinha; o resto da lateral fica parado. */}
      <div className="flex-1 min-h-0 flex flex-col">
        <CategoryNav categories={categories} />
      </div>

      <div className="border-t border-gray-100 p-4 space-y-3 shrink-0">
        <TrocarUnidade />
        <LanguagePicker variant="lista" />
        <button
          type="button"
          onClick={() => window.openStoreInfo?.()}
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-[11px] font-bold text-gray-600 hover:bg-gray-100 transition"
        >
          <i className="fas fa-store text-gray-400" /> {t('Sobre a loja')}
        </button>
      </div>
    </aside>
  );
}
